class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.closeTimeout = null;
    this.initMegaMenuHover();
    this.initTopLevelHover();
  }

  initTopLevelHover() {
    // Add hover listeners for desktop - use the entire header-menu element
    // to include both the summary and the dropdown content
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    
    // Also track the dropdown content separately for better hover detection
    const content = this.querySelector('.mega-menu__content, .header__submenu');
    if (content) {
      content.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      content.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    }
  }

  onMouseEnter() {
    // Only trigger hover open on desktop (990px+)
    if (window.innerWidth >= 990) {
      // Clear any pending close timeout
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
      this.mainDetailsToggle.setAttribute('open', '');
    }
  }

  onMouseLeave() {
    // Only trigger hover close on desktop (990px+)
    if (window.innerWidth >= 990) {
      // Add a small delay before closing to allow moving to submenu
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
      }
      this.closeTimeout = setTimeout(() => {
        this.close();
        this.closeTimeout = null;
      }, 150);
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  initMegaMenuHover() {
    const columnsContainer = this.querySelector('.mega-menu__columns');
    if (!columnsContainer) return;

    const level2Items = columnsContainer.querySelectorAll('.mega-menu__item[data-level-2-index]');
    const level3Column = columnsContainer.querySelector('.mega-menu__column--level-3');
    const level4Column = columnsContainer.querySelector('.mega-menu__column--level-4');
    const level3Lists = columnsContainer.querySelectorAll('.mega-menu__list--level-3');
    const level4Lists = columnsContainer.querySelectorAll('.mega-menu__list--level-4');

    // Track current active level 2 item and timeout for switching
    let currentLevel2Index = null;
    let level2SwitchTimeout = null;

    // Set level 3 column position once (fixed position)
    let level3ColumnPositioned = false;
    const positionLevel3Column = () => {
      if (level3ColumnPositioned || !level3Column) return;
      
      const level2Col = columnsContainer.querySelector('.mega-menu__column--level-2');
      const level2List = level2Col ? level2Col.querySelector('.mega-menu__list--level-2') : null;
      const megaMenuContent = columnsContainer.closest('.mega-menu__content');
      
      if (level2Col && level2List && megaMenuContent) {
        const level2Rect = level2Col.getBoundingClientRect();
        const level2ListRect = level2List.getBoundingClientRect();
        const containerRect = columnsContainer.getBoundingClientRect();
        const contentRect = megaMenuContent.getBoundingClientRect();
        // Position column: gap (4rem) + margin-left (4rem) = 8rem total spacing
        level3Column.style.left = `${level2Rect.right - containerRect.left}px`;
        // Position column to align with top of level 2 list (fixed)
        level3Column.style.top = `${level2ListRect.top - containerRect.top}px`;
        // Match height from level 2 list top to content bottom (fixed)
        level3Column.style.height = `${contentRect.bottom - level2ListRect.top}px`;
        level3ColumnPositioned = true;
      }
    };

    // Function to show a specific level 3 list
    const showLevel3ForIndex = (level2Index) => {
      const level3List = Array.from(level3Lists).find(
        (list) => list.getAttribute('data-level-2-index') === level2Index
      );

      // Hide all level 3 lists first (remove is-active class)
      level3Lists.forEach((list) => {
        list.classList.remove('is-active');
      });
      
      // Hide all level 4 lists
      level4Lists.forEach((list) => {
        list.classList.remove('is-active');
      });
      
      // Hide level 4 column
      if (level4Column) {
        level4Column.classList.remove('is-active');
      }
      
      // If this item has children (a matching level 3 list), show them
      if (level3List) {
        // Position column once if not already positioned
        positionLevel3Column();
        
        // Show the matching level 3 list (add is-active class to override CSS :has())
        level3List.classList.add('is-active');
        
        // Show level 3 column
        if (level3Column) {
          level3Column.classList.add('is-active');
        }
      } else {
        // No children - hide level 3 column
        if (level3Column) {
          level3Column.classList.remove('is-active');
        }
      }
      
      currentLevel2Index = level2Index;
    };

    // Handle level 2 hover to show level 3 (or hide if no children)
    level2Items.forEach((item) => {
      const level2Index = item.getAttribute('data-level-2-index');

      item.addEventListener('mouseenter', () => {
        // Clear any pending switch
        if (level2SwitchTimeout) {
          clearTimeout(level2SwitchTimeout);
          level2SwitchTimeout = null;
        }
        
        // If this is the first hover or same item, show immediately
        if (currentLevel2Index === null || currentLevel2Index === level2Index) {
          showLevel3ForIndex(level2Index);
        } else {
          // Delay switching to allow diagonal mouse movement to level 3
          level2SwitchTimeout = setTimeout(() => {
            showLevel3ForIndex(level2Index);
            level2SwitchTimeout = null;
          }, 300);
        }
      });
    });

    // Cancel switch timeout when entering level 3 column (user made it to the submenu)
    if (level3Column) {
      level3Column.addEventListener('mouseenter', () => {
        if (level2SwitchTimeout) {
          clearTimeout(level2SwitchTimeout);
          level2SwitchTimeout = null;
        }
      });
    }

    // Set level 4 column position once (fixed position)
    let level4ColumnPositioned = false;
    const positionLevel4Column = () => {
      if (level4ColumnPositioned || !level4Column || !level3Column) return;
      
      const level3Rect = level3Column.getBoundingClientRect();
      const containerRect = columnsContainer.getBoundingClientRect();
      const megaMenuContent = columnsContainer.closest('.mega-menu__content');
      const level2List = columnsContainer.querySelector('.mega-menu__list--level-2');
      
      if (level2List && megaMenuContent) {
        const level2ListRect = level2List.getBoundingClientRect();
        const contentRect = megaMenuContent.getBoundingClientRect();
        // Position column: gap (4rem) + margin-left (4rem) = 8rem total spacing
        level4Column.style.left = `${level3Rect.right - containerRect.left}px`;
        // Position column to align with top of level 2 list (same as level 3)
        level4Column.style.top = `${level2ListRect.top - containerRect.top}px`;
        // Match height from level 2 list top to content bottom (same as level 3)
        level4Column.style.height = `${contentRect.bottom - level2ListRect.top}px`;
        level4ColumnPositioned = true;
      }
    };

    // Handle level 3 hover to show level 4 (or hide if no children)
    const level3Items = columnsContainer.querySelectorAll('.mega-menu__item[data-level-3-index]');
    level3Items.forEach((item) => {
      const level3Index = item.getAttribute('data-level-3-index');
      const level2Index = item.closest('.mega-menu__list--level-3')?.getAttribute('data-level-2-index');
      const level4List = Array.from(level4Lists).find(
        (list) =>
          list.getAttribute('data-level-2-index') === level2Index &&
          list.getAttribute('data-level-3-index') === level3Index
      );

      item.addEventListener('mouseenter', () => {
        // Hide all level 4 lists first
        level4Lists.forEach((list) => {
          list.classList.remove('is-active');
        });
        
        // If this item has children (a matching level 4 list), show them
        if (level4List) {
          // Position column once if not already positioned
          positionLevel4Column();
          
          // Show the matching level 4 list
          level4List.classList.add('is-active');
          
          // Show level 4 column
          if (level4Column) {
            level4Column.classList.add('is-active');
          }
        } else {
          // No children - hide level 4 column
          if (level4Column) {
            level4Column.classList.remove('is-active');
          }
        }
      });
    });

    // Hide columns when mouse leaves the columns container
    // BUT check if we're moving to a child element (like level 3 column which is absolutely positioned)
    columnsContainer.addEventListener('mouseleave', (event) => {
      // Check if we're moving to level 3 or level 4 column (which are absolutely positioned children)
      const relatedTarget = event.relatedTarget;
      if (relatedTarget) {
        if (level3Column && level3Column.contains(relatedTarget)) return;
        if (level4Column && level4Column.contains(relatedTarget)) return;
        // Also check if moving to the mega menu content area
        const megaMenuContent = columnsContainer.closest('.mega-menu__content');
        if (megaMenuContent && megaMenuContent.contains(relatedTarget)) return;
      }
      
      // Clear any pending switch timeout
      if (level2SwitchTimeout) {
        clearTimeout(level2SwitchTimeout);
        level2SwitchTimeout = null;
      }
      
      if (level3Column) {
        level3Column.classList.remove('is-active');
      }
      if (level4Column) {
        level4Column.classList.remove('is-active');
      }
      level3Lists.forEach((list) => {
        list.classList.remove('is-active');
      });
      level4Lists.forEach((list) => {
        list.classList.remove('is-active');
      });
      // Reset positioning flags and tracking so columns can be repositioned if menu reopens
      level3ColumnPositioned = false;
      level4ColumnPositioned = false;
      currentLevel2Index = null;
    });
    
    // Also add mouseleave to level 3 column itself
    if (level3Column) {
      level3Column.addEventListener('mouseleave', (event) => {
        const relatedTarget = event.relatedTarget;
        // If moving back to columns container or level 4, don't hide
        if (relatedTarget) {
          if (columnsContainer.contains(relatedTarget)) return;
          if (level4Column && level4Column.contains(relatedTarget)) return;
        }
        // Otherwise hide level 3
        level3Column.classList.remove('is-active');
        level3Lists.forEach((list) => {
          list.classList.remove('is-active');
        });
        currentLevel2Index = null;
      });
    }
  }
}

customElements.define('header-menu', HeaderMenu);
